"use client";

import { useEffect, type RefObject } from "react";
import { useSearch } from "./useSearch";
import { SearchInputField } from "./SearchInputField";
import { SearchResultsDropdown } from "./SearchResultsDropdown";

interface SearchInputProps {
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  /**
   * When this value changes, the input will be cleared (query/results/loading).
   * Useful for "close" actions in parent UI (e.g. mobile expandable search).
   */
  clearSignal?: number;
  /** Called when user clicks a result link. */
  onSelectResult?: () => void;
}

export function SearchInput({
  autoFocus,
  inputRef,
  clearSignal,
  onSelectResult,
}: SearchInputProps) {
  const {
    query,
    setQuery,
    gamesResults,
    publishersResults,
    loading,
    expandedGames,
    setExpandedGames,
    expandedPublishers,
    setExpandedPublishers,
    clearQuery,
  } = useSearch();

  useEffect(() => {
    if (clearSignal === undefined) return;
    clearQuery();
  }, [clearSignal, clearQuery]);

  const handleSelect = () => {
    clearQuery();
    onSelectResult?.();
  };

  return (
    <div className="relative w-full min-w-0">
      <SearchInputField
        query={query}
        loading={loading}
        inputRef={inputRef}
        autoFocus={autoFocus}
        onQueryChange={setQuery}
        onClear={clearQuery}
      />

      <SearchResultsDropdown
        query={query}
        loading={loading}
        games={gamesResults}
        publishers={publishersResults}
        expandedGames={expandedGames}
        expandedPublishers={expandedPublishers}
        onToggleGamesExpand={() => setExpandedGames(!expandedGames)}
        onTogglePublishersExpand={() =>
          setExpandedPublishers(!expandedPublishers)
        }
        onSelect={handleSelect}
      />
    </div>
  );
}
